// Automatic applicant review (written by Dispatch on apply). Same quick 0–100
// score and red / yellow / green per part and criterion as the owner email.
// Colour is never the only signal: every status carries a word.
import { useState } from "react";
import { T, F, Over, Pill } from "@/lib/bricks";

export const STATUS = {
  green:  { word: "Good",    fg: T.ok,      fgDark: "#B8CC9E", tint: T.okTint,    tintDark: "rgba(230,236,221,0.10)", tone: "ok" },
  yellow: { word: "Check",   fg: T.warn,    fgDark: "#E6B477", tint: T.warnTint,  tintDark: "rgba(246,232,209,0.10)", tone: "warn" },
  red:    { word: "Weak",    fg: T.bad,     fgDark: "#E39A8E", tint: T.badTint,   tintDark: "rgba(241,218,212,0.10)", tone: "bad" },
  grey:   { word: "Missing", fg: T.inkMute, fgDark: T.moonMute, tint: T.paperSoft, tintDark: "rgba(244,241,235,0.06)", tone: "neutral" },
};
const SOURCE = { verified: "Verified", self_reported: "Self-reported", couldnt_check: "Couldn't check" };
const PART_ORDER = ["samples", "gear", "availability", "location", "portfolio", "drone"];

export function statusOf(colour) {
  return STATUS[colour] || STATUS.grey;
}

export function verdictText(r) {
  if (!r) return "";
  if (r.colour === "grey") return "Incomplete";
  return r.verdict_label || "Needs review";
}

function fmtPts(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function Square({ colour, size = 9, dark }) {
  const s = statusOf(colour);
  return <span aria-hidden="true" style={{ display: "inline-block", width: size, height: size, borderRadius: 2, background: dark ? s.fgDark : s.fg, flexShrink: 0 }} />;
}

// Hiring list: "72 · Needs review" in the verdict colour.
export function ReviewScorePill({ review, dark }) {
  if (!review) return null;
  const s = statusOf(review.colour);
  const label = verdictText(review);
  return (
    <span data-testid="applicant-score" title={`Automatic review · ${label}${review.score != null ? ` · ${review.score}/100` : ""}`} style={{ flexShrink: 0 }}>
      <Pill tone={s.tone} dark={dark}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Square colour={review.colour} dark={dark} size={8} />
          {review.score != null ? `${review.score} · ` : ""}{label}
        </span>
      </Pill>
    </span>
  );
}

function SourceTag({ source, p, dark }) {
  const colour = source === "verified" ? (dark ? STATUS.green.fgDark : T.ok) : source === "couldnt_check" ? p.ink3 : p.ink2;
  return (
    <span style={{ fontFamily: F.display, fontSize: 12, color: colour, whiteSpace: "nowrap", flexShrink: 0, minWidth: 92 }}>
      {SOURCE[source] || "Couldn't check"}
    </span>
  );
}

function fmtWhen(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Toronto" }) + " ET";
  } catch { return ""; }
}

function orderedParts(parts) {
  const list = Array.isArray(parts)
    ? parts
    : Object.entries(parts || {}).map(([key, v]) => ({ key, ...v }));
  const rank = (k) => { const i = PART_ORDER.indexOf(k); return i === -1 ? 99 : i; };
  return [...list].sort((a, b) => rank(a.key) - rank(b.key));
}

function orderedCriteria(criteria) {
  return Array.isArray(criteria)
    ? criteria
    : Object.entries(criteria || {}).map(([key, v]) => ({ key, ...v }));
}


const CRIT_LABEL = {
  verticals: "Straight verticals", exposure: "Exposure / HDR", windows: "Window pulls",
  white_balance: "White balance", composition: "Composition", clutter: "Clutter", sharpness: "Sharpness",
};

function imageStatus(img) {
  if (!img.reviewed) return ["grey", img.error === "not_an_image" ? "Not an image" : "Not reviewed"];
  if (img.is_real_estate === false) return ["red", "Not real estate"];
  const q = Number(img.quality);
  if (q >= 0.75) return ["green", "Good"];
  if (q >= 0.5) return ["yellow", "Check"];
  return ["red", "Weak"];
}

// Every sample the review looked at, with its own R/Y/G word.
export function SampleGrid({ samples, needsSamples, p, dark }) {
  const [open, setOpen] = useState(null);
  const list = samples || [];
  if (!list.length) {
    return (
      <div style={{ fontSize: 13, color: p.ink3 }}>
        {needsSamples ? "No sample photos uploaded. Needs samples." : "No images were reviewed."}
      </div>
    );
  }
  return (
    <div data-testid="review-samples" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(132px,1fr))", gap: 8 }}>
      {list.map((img, i) => {
        const [status, word] = imageStatus(img);
        const active = open === i;
        return (
          <div key={i} style={{ border: `1px solid ${active ? T.brick : p.hair}`, borderRadius: 2, background: p.surf2, overflow: "hidden" }}>
            <button type="button" onClick={() => setOpen(active ? null : i)} style={{ display: "block", width: "100%", padding: 0, border: "none", background: "none", cursor: "pointer" }} aria-label={`Sample ${i + 1}: ${word}`}>
              <img src={img.thumb_url || img.url} alt={img.note || `Sample ${i + 1}`} loading="lazy" style={{ display: "block", width: "100%", aspectRatio: "3 / 2", objectFit: "cover" }} />
            </button>
            <div style={{ padding: "6px 8px", display: "flex", alignItems: "center", gap: 6 }}>
              <Square colour={status} dark={dark} size={8} />
              <span style={{ fontFamily: F.display, fontSize: 11.5, color: p.ink2 }}>
                {word}{img.pick ? ` · ${img.pick}` : ""}{img.origin === "portfolio" ? " · site" : ""}
              </span>
            </div>
            {active && (
              <div style={{ padding: "0 8px 8px", fontSize: 11, lineHeight: 1.45, color: p.ink3 }}>
                {img.note && <div>{img.note}</div>}
                {img.fails?.length > 0 && <div style={{ color: dark ? STATUS.red.fgDark : T.bad }}>Fails: {img.fails.map((k) => CRIT_LABEL[k] || k).join(", ")}</div>}
                {img.minors?.length > 0 && <div>Minor: {img.minors.map((k) => CRIT_LABEL[k] || k).join(", ")}</div>}
                {img.camera && <div>Camera (photo metadata): {img.camera}</div>}
                {img.watermark && <div>Watermark or logo on the image</div>}
                <a href={img.url} target="_blank" rel="noreferrer" style={{ color: T.brick }}>Open full size</a>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const DAY = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const WIN = { early_morning: "Early morning (6–9)", daytime: "Daytime (9–5)", evening: "Evening (5–9)" };

// Structured availability from the apply form (vendor_availability, self-reported).
export function availabilityLines(av) {
  if (!av || !(av.days_of_week || []).length) return null;
  const days = av.days_of_week;
  const wk = days.filter((d) => ["mon", "tue", "wed", "thu", "fri"].includes(d));
  let dayTxt = wk.length === 5 ? "Mon–Fri" : (wk.map((d) => DAY[d]).join(", ") || "No weekdays");
  const we = ["sat", "sun"].filter((d) => days.includes(d)).map((d) => DAY[d]);
  dayTxt += we.length ? ` + ${we.join("/")}` : " · no weekends";
  const h = av.notice_hours;
  const notice = h == null ? "—" : h < 24 ? `${h} hours` : `${h / 24} day${h === 24 ? "" : "s"}`;
  const yn = (b) => (b === true ? "Yes" : b === false ? "No" : "—");
  return [
    ["Days", dayTxt],
    ["Time windows", (av.time_windows || []).map((w) => WIN[w] || w).join(", ") || "—"],
    ["Shoots / week", av.shoots_per_week ?? "—"],
    ["Max / day", av.jobs_per_day ?? "—"],
    ["Notice", notice],
    ["Earliest start", av.earliest_start_date || "—"],
    ["Car", yn(av.has_car)],
    ["Licence", yn(av.has_licence)],
  ];
}

export function ApplicantReviewSection({ review, p, dark }) {
  if (!review) {
    return (
      <div data-testid="applicant-review" style={{ fontSize: 13.5, color: p.ink3 }}>
        No automatic review yet.
      </div>
    );
  }
  const s = statusOf(review.colour);
  const fg = dark ? s.fgDark : s.fg;
  const parts = orderedParts(review.parts);
  const crit = orderedCriteria(review.criteria);
  const flags = review.flags || [];
  const evidence = review.evidence || [];
  const missing = review.missing || [];
  const capped = review.incomplete || review.score_cap != null;

  return (
    <section data-testid="applicant-review" style={{ borderTop: `1px solid ${p.hair}` }}>
      {/* Score band */}
      <div style={{ padding: "16px 0 4px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 12.5, color: p.ink3, display: "flex", alignItems: "center", gap: 7 }}>
              <Square colour={review.colour} dark={dark} /> Automatic review{review.reviewed_at ? ` · ${fmtWhen(review.reviewed_at)}` : ""}
            </div>
            <div data-testid="review-verdict" style={{ fontSize: 22, fontWeight: 500, letterSpacing: -0.4, marginTop: 4, color: p.ink }}>
              {review.colour === "grey" ? `Incomplete · ${review.verdict_label || "Needs review"}` : verdictText(review)}
            </div>
            {capped && (
              <div style={{ fontSize: 12, color: p.ink2, marginTop: 2 }}>
                Incomplete. Score capped at {review.score_cap ?? 60} until{review.needs_samples ? " sample photos are uploaded and" : ""} everything is reviewed.
              </div>
            )}
          </div>
          <div data-testid="review-score" style={{ fontFamily: F.mono, fontWeight: 500, fontSize: 30, letterSpacing: -1, lineHeight: 1, color: p.ink }}>
            {review.score ?? "—"}<span style={{ fontFamily: F.mono, fontSize: 13, letterSpacing: 0, color: p.ink3 }}>/100</span>
          </div>
        </div>

        {parts.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 8, marginTop: 14 }}>
            {parts.map((part) => {
              const ps = statusOf(part.status);
              return (
                <div key={part.key} data-testid={`review-part-${part.key}`} style={{ display: "flex", minWidth: 0 }}>
                  <div style={{ padding: "4px 0", minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: p.ink }}>{part.label}</div>
                    <div style={{ fontSize: 12, color: dark ? ps.fgDark : ps.fg, marginTop: 2 }}>
                      {part.status_word || ps.word} · <span style={{ fontFamily: F.mono, fontSize: 11 }}>{fmtPts(part.points)}/{fmtPts(part.max_points)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ padding: "16px 0 0", display: "flex", flexDirection: "column", gap: 22 }}>
        <div>
          <Over style={{ display: "block", marginBottom: 10 }}>Sample photos{(review.samples || []).length ? ` · ${review.samples.length}` : ""}</Over>
          <SampleGrid samples={review.samples} needsSamples={review.needs_samples} p={p} dark={dark} />
        </div>

        {!review.vision_ran && (
          <div style={{ fontSize: 13, color: p.ink3 }}>Photo rubric not run: no sample photos were reviewed.</div>
        )}
        {crit.length > 0 && review.vision_ran && (
          <div>
            <Over style={{ display: "block", marginBottom: 10 }}>Photo rubric</Over>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {crit.map((c) => {
                const cs = statusOf(c.status);
                return (
                  <div key={c.key || c.label} data-testid={`review-criterion-${c.key}`} style={{ display: "flex", alignItems: "baseline", gap: 10, fontSize: 13 }}>
                    <span style={{ alignSelf: "center", display: "flex" }}><Square colour={c.status} dark={dark} /></span>
                    <span style={{ minWidth: 150, color: p.ink }}>{c.label}</span>
                    <span style={{ fontSize: 12.5, color: dark ? cs.fgDark : cs.fg, minWidth: 56 }}>{c.status_word || cs.word}</span>
                    <span style={{ color: p.ink3 }}>{c.words}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {(flags.length > 0 || evidence.length > 0) && (
          <div>
            <Over style={{ display: "block", marginBottom: 10 }}>Evidence</Over>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {flags.map((b, i) => (
                <div key={`f${i}`} data-testid="review-flag" style={{ display: "flex", alignItems: "baseline", gap: 10, fontSize: 13 }}>
                  <SourceTag source={b.source} p={p} dark={dark} />
                  <span style={{ color: dark ? STATUS.red.fgDark : T.bad }}>{b.text}</span>
                </div>
              ))}
              {evidence.map((b, i) => (
                <div key={`e${i}`} data-testid="review-evidence" style={{ display: "flex", alignItems: "baseline", gap: 10, fontSize: 13 }}>
                  <SourceTag source={b.source} p={p} dark={dark} />
                  <span style={{ color: b.ok === false ? (dark ? STATUS.red.fgDark : T.bad) : p.ink2 }}>{b.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {missing.length > 0 && (
          <div>
            <Over style={{ display: "block", marginBottom: 10 }}>Still missing</Over>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: p.ink2, display: "flex", flexDirection: "column", gap: 4 }}>
              {missing.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {review.availability_summary && (
          <div style={{ fontSize: 13, color: p.ink3 }}>
            <span style={{ fontWeight: 500, color: p.ink2, marginRight: 8 }}>Availability</span>
            {review.availability_summary}
          </div>
        )}

        <div style={{ fontSize: 12, color: p.ink4 }}>
          {review.vision_ran ? "Sample photos were checked automatically." : "Sample photos were not checked automatically."} Verified = checked by Bricks · Self-reported = applicant's word · Couldn't check = not confirmed.
        </div>
      </div>
    </section>
  );
}
